import * as React from 'react'

/**
 * Track a CSS media query from React.
 *
 * Most responsive behavior belongs in CSS, and everywhere it can be it is.
 * This exists for the cases where a breakpoint changes *structure* rather than
 * style — the survey screen moves the tree editor from a bottom sheet into a
 * side inspector, and rendering both and hiding one with `lg:hidden` would mean
 * two live copies of a component that holds its own state. A half-typed
 * estimate would sit in whichever copy was hidden.
 *
 * `useSyncExternalStore` rather than an effect: it reads the current value
 * during the first render, so the layout does not flash through the phone
 * arrangement on a desktop load.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query)
      list.addEventListener('change', onChange)
      return () => {
        list.removeEventListener('change', onChange)
      }
    },
    [query],
  )

  return React.useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    // Server snapshot: assume the narrow layout. This app renders in a browser
    // only, but the narrow arrangement is the safe default either way.
    () => false,
  )
}

/**
 * The breakpoint at which map screens gain a side inspector.
 *
 * Deliberately the same `lg` the app shell uses to swap its drawer for an
 * inline nav, so the whole product changes shape at one width rather than
 * three.
 */
export const DESKTOP_QUERY = '(min-width: 1024px)'
